import { Box, Button, Typography, List, ListItem, useTheme } from '@mui/material';
import { Icon24CheckmarkLarge } from '@care/react-embrace-icons';
import Image from 'next/image';
import { WithLazyLoadProps } from '@/utilities/withLazyLoad';
import useResponsive from '@/components/hooks/useResponsive';
import { AnalyticsEventName, CtaLocation, CtaText, ScreenName } from '@/types/enums';
import { AnalyticsHelper } from '@/utilities/AnalyticsHelper';
import { VERTICALS_NAMES } from '@/constants';
import mobile from '@/../public/greaterCare/safety/mobile.webp';
import tablet from '@/../public/greaterCare/safety/tablet.webp';
import desktop from '@/../public/greaterCare/safety/desktop.webp';

const bulletPoints = [
  'All individual caregivers on Care.com start with a required background check\u00A0\u00B9',
  'Monitored job posts, messages and a 24/7 safety hotline',
  'Tips and resources to help you make safer hiring choices',
];

function Safety({ hasLoaded }: Readonly<WithLazyLoadProps>) {
  const { isDesktop, isMobile, isTablet } = useResponsive();
  const theme = useTheme();

  if (!hasLoaded) {
    return null;
  }

  const handleOnClick = () => {
    AnalyticsHelper.logEvent({
      name: AnalyticsEventName.CTA_INTERACTED_VHP,
      data: {
        screen_name: ScreenName.VISITOR_HOMEPAGE,
        enrollment_initiated: false,
        cta_text: CtaText.LEARN_MORE,
        cta_location: CtaLocation.VHP_BODY,
        vertical: VERTICALS_NAMES.unknown,
      },
    });
    window.open('https://www.care.com/about/safety/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      sx={{
        justifySelf: 'center',
        padding: {
          xs: theme.spacing(6, 3),
          md: theme.spacing(6, 10.5, 8),
          lg: theme.spacing(12, 10.5),
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
          maxWidth: { xs: '512px', md: '1272px' },
        }}>
        {isDesktop && (
          <Box
            minWidth="509px"
            height="420px"
            sx={{
              minWidth: '509px',
              height: '420px',
              marginRight: 8,
              [theme.breakpoints.down(1050)]: {
                minWidth: '450px',
                height: '371px',
                marginRight: 5,
              },
              display: 'flex',
              alignSelf: 'flex-start',
              position: 'relative',
            }}>
            <Image src={desktop} alt="CareProtect" layout="fill" />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            maxWidth: { md: '533px' },
            minWidth: { md: '413px' },
          }}>
          <Typography
            variant={isMobile ? 'display3' : 'display2'}
            sx={{
              margin: 0,
            }}>
            Safety is at the heart of our community
          </Typography>

          <List disablePadding sx={{ my: 4 }}>
            {bulletPoints.map((text, index) => (
              <ListItem
                key={text}
                sx={{
                  mt: index ? 2 : 0,
                  padding: 0,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignSelf: 'flex-start',
                  }}>
                  <Icon24CheckmarkLarge
                    width="22px"
                    height="22px"
                    color={theme.palette.icon.brand}
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    ml: 2,
                  }}>
                  {text}
                </Typography>
              </ListItem>
            ))}
          </List>

          {isMobile && (
            <Box margin="auto">
              <Image src={mobile} alt="CareProtect" width="327px" height="276px" />
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleOnClick}
            color="primary"
            size="large"
            fullWidth={isMobile}
            sx={{
              justifyContent: 'center',
              width: { sm: '171px' },
              mt: { xs: 4, sm: 0 },
            }}>
            Learn more
          </Button>

          {isTablet && (
            <Box mt={4}>
              <Image src={tablet} alt="CareProtect" width="470px" height="336px" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Safety;
